import { useState, useCallback } from 'react';
import { useCompany } from '@/hooks/company/use-company';
import { useAuthUser } from '@/modules/auth/context/AuthContext';
import { useActiveProductProperties } from '@/modules/loan-product-settings/hooks/useProductProperties';
import { useGetCustomer, useSearchCustomers, useGetLoansProducts } from '@/shared/api/client/customers';
import { ProductPropertiesDTO, CustomerDTO, ProductDTO } from '@/shared/api/model';

export interface SimulatorLoanData {
    branch: string;
    custNo: string;
    name: string;
    glNo: string;
    productName: string;
    loanAmount: number;
    loanPeriod: number;
    intRate: number;
    institutionName: string;
    employerName: string;
}

export const useFlowSimulatorData = () => {
    const authUser = useAuthUser();
    const branch = authUser?.branch || '';

    const { data: companyData, isLoading: isCompanyLoading } = useCompany();
    const { data: activeProductsResponse, isLoading: isProductsLoading } = useActiveProductProperties();

    const [customerSearch, setCustomerSearch] = useState('');
    const [selectedCustNo, setSelectedCustNo] = useState('');
    const [selectedGlNo, setSelectedGlNo] = useState('');

    const { data: searchResults, isFetching: isSearching } = useSearchCustomers(
        branch,
        { query: customerSearch },
        { query: { enabled: !!branch && customerSearch.length >= 2 } }
    );

    const { data: customerResponse } = useGetCustomer(
        branch,
        selectedCustNo,
        { query: { enabled: !!branch && !!selectedCustNo } }
    );

    const { data: loanProductsResponse, isLoading: isLoanProductsLoading } = useGetLoansProducts(
        branch,
        selectedCustNo,
        { query: { enabled: !!branch && !!selectedCustNo } }
    );

    const activeProducts: ProductPropertiesDTO[] = activeProductsResponse || [];
    const customers: CustomerDTO[] = searchResults || [];
    const customer: CustomerDTO | undefined = customerResponse;
    const loanProducts: ProductDTO[] = loanProductsResponse || [];

    const companyName = companyData?.brFname || companyData?.brName || '';

    const buildAppapproDTO = useCallback((overrides: Partial<SimulatorLoanData> = {}): any => {
        const selectedProduct = activeProducts.find(p => p.glNo === (overrides.glNo || selectedGlNo));

        return {
            id: 0,
            loanId: 0,
            loanAmount: overrides.loanAmount ?? 5000000,
            recommendedLoanAmt: overrides.loanAmount ?? 5000000,
            lnPeriod: overrides.loanPeriod ?? selectedProduct?.defaultTerm ?? 24,
            recommendedLnPeriod: overrides.loanPeriod ?? selectedProduct?.defaultTerm ?? 24,
            intRate: overrides.intRate ?? selectedProduct?.baseInterestRate ?? 12,
            glNo: overrides.glNo || selectedGlNo || selectedProduct?.glNo || '',
            branch: branch,
            custNo: overrides.custNo || selectedCustNo || '',
            name: overrides.name || customer?.name || '',
            productName: overrides.productName || selectedProduct?.productName || '',
            employerName: overrides.employerName || '',
            institutionName: companyName,
            status: '0',
            attachments: [],
            guarantors: [],
        };
    }, [activeProducts, selectedGlNo, selectedCustNo, customer, companyName, branch]);

    return {
        companyName,
        companyData,
        isCompanyLoading,
        branch,
        activeProducts,
        isProductsLoading,
        customers,
        isSearching,
        customerSearch,
        setCustomerSearch,
        selectedCustNo,
        setSelectedCustNo,
        selectedGlNo,
        setSelectedGlNo,
        customer,
        loanProducts,
        isLoanProductsLoading,
        buildAppapproDTO,
    };
};
